import { inject, Injectable } from "@angular/core";
import { ComparatorOperators, ConformanceToKiRsStandardAttestationField, ConformanceToKiRsStandardAttestationInput, ConformanceToKiRsStandardAttestationQueryComplexInput, ConformanceToKiRsStandardAttestationSimpleQueryInput, CreateAndSignKirsAssessmentGQL, CreateAndSignKirsAssessmentMutation, ExportKirsAssessmentGQL, ExportKirsAssessmentQuery, GetKirsAssessmentGQL, GetKirsAssessmentQuery, OperatorInput, PagingInput } from "../../../graphql/generated";
import { map, Observable } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class KirsAssessmentService {
    private readonly GetKirsAssessmentGQL: GetKirsAssessmentGQL = inject(GetKirsAssessmentGQL);
    private readonly CreateOrUpdateKirsAssessmentGQL: CreateAndSignKirsAssessmentGQL = inject(CreateAndSignKirsAssessmentGQL);
    private readonly ExportKirsAssessmentGQL: ExportKirsAssessmentGQL = inject(ExportKirsAssessmentGQL);

    getKirsAssessment(campaignId: string, attestationId: string | null, legalEntityIds: string[] | null, parameters: any): Observable<GetKirsAssessmentQuery['getConformanceKIRsAttestation']> {
        const params = convertLazyLoadToComplex(parameters)
        return this.GetKirsAssessmentGQL.fetch({ campaignId, attestationId: attestationId ?? null, legalEntityIds: legalEntityIds ?? null, page: params.page, query: params.query }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getConformanceKIRsAttestation))
    }

    createAndSignKirsAssessment(campaignId: string, legalEntityTrackingIds: string[], input: ConformanceToKiRsStandardAttestationInput): Observable<CreateAndSignKirsAssessmentMutation['createAndSignConformanceToKIRsAttestation']> {
        return this.CreateOrUpdateKirsAssessmentGQL.mutate({ campaignId, input, legalEntityTrackingIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createAndSignConformanceToKIRsAttestation))
    }

    exportKirsAssessment(campaignId: string, attestationId: string, legalEntityIds: string[] | null, timeZone: string, parameters: any): Observable<ExportKirsAssessmentQuery['downloadConformanceKIRsAttestationExcel']> {
        const params = convertLazyLoadToComplex(parameters)
        return this.ExportKirsAssessmentGQL.fetch({ attestationId, campaignId, legalEntityIds: legalEntityIds ?? null, query: params.query, timeZone }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.downloadConformanceKIRsAttestationExcel))
    }
}

export function convertLazyLoadToComplex(parameters: any): { query: ConformanceToKiRsStandardAttestationQueryComplexInput, page: PagingInput } {
    const query: ConformanceToKiRsStandardAttestationSimpleQueryInput[] = [];
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_KIRS_ASSESSMENT[field as keyof typeof ENUM_KIRS_ASSESSMENT]).forEach(key => {

                        query.push({
                            field: key as ConformanceToKiRsStandardAttestationField,
                            filters: {
                                operator: [ConformanceToKiRsStandardAttestationField.RatingQuantitative]?.includes(key) ? ComparatorOperators.Eq : ComparatorOperators.Match,
                                value: (key === ConformanceToKiRsStandardAttestationField.RatingQualitative ? values.map(v => String(v)?.replace(' ', '_')) : values)?.map(v => String(v)?.toLowerCase() === 'empty' ? null : v)
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query,
                    operator: OperatorInput.And
                }
            ]
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 20
        }
    }
}

export const ENUM_KIRS_ASSESSMENT = {
    title: [ConformanceToKiRsStandardAttestationField.Title],
    kir: [ConformanceToKiRsStandardAttestationField.Kir],
    ratingQuantitative: [ConformanceToKiRsStandardAttestationField.RatingQuantitative],
    ratingQualitative: [ConformanceToKiRsStandardAttestationField.RatingQualitative],
    assessmentDescriptionExplanation: [ConformanceToKiRsStandardAttestationField.AssesmentDescriptionExplanation],
    supportingEvidences: [ConformanceToKiRsStandardAttestationField.SupportingEvidence],
    actionPlanDescription: [ConformanceToKiRsStandardAttestationField.ActionPlanDescription],
    actionPlanResponsible: [ConformanceToKiRsStandardAttestationField.ActionPlanResponsible],
    actionPlanDeadline: [ConformanceToKiRsStandardAttestationField.ActionPlanDeadline],
    attestationStatus: [ConformanceToKiRsStandardAttestationField.AttestationStatus],
    status: [ConformanceToKiRsStandardAttestationField.AttestationStatus]
}